/**
 * @fileoverview Message composer with multi-channel support
 * @module ctl-compose
 * @version 1.0.0
 * 
 * @description
 * A multi-channel message composer supporting chat, DM, email, and SMS.
 * Includes character counting, subject line support, quick replies, and attachments.
 * 
 * @example
 * <ctl-compose channel="sms" persona="support"></ctl-compose>
 * 
 * @example
 * <ctl-compose channel="email" subject-enabled></ctl-compose>
 */

const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    --ctl-font: var(--ctl-font-sans, system-ui,-apple-system,Segoe UI,Roboto,Arial);
    --ctl-radius: var(--ctl-radius-lg, 14px);
    --ctl-shadow: var(--ctl-shadow-sm, 0 6px 18px rgba(0,0,0,.10));
    --ctl-focus-ring: var(--ctl-focus-ring, 0 0 0 3px rgba(79,70,229,.15));

    display:block;
    font-family: var(--ctl-font);
    color: var(--ctl-color-text);
  }

  .frame{
    border: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
    border-radius: var(--ctl-radius);
    box-shadow: var(--ctl-shadow);
    overflow: hidden;
  }

  .subject-row{
    display: none;
    border-bottom: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
    padding: var(--ctl-space-2-5) var(--ctl-space-3);
  }
  :host([subject-enabled]) .subject-row{ display: block; }

  .subject-input{
    width: 100%;
    border: 0;
    outline: 0;
    font-family: inherit;
    font-size: var(--ctl-font-size-md);
    font-weight: var(--ctl-font-weight-extrabold);
    color: var(--ctl-color-text);
    background: transparent;
  }
  .subject-input::placeholder{
    color: var(--ctl-color-muted);
    font-weight: var(--ctl-font-weight-bold);
  }
  .subject-input:focus{
    outline: none;
  }

  .compose-area{
    position: relative;
    background: var(--ctl-color-surface);
  }

  .textarea{
    width: 100%;
    min-height: 120px;
    max-height: 320px;
    border: 0;
    outline: 0;
    padding: var(--ctl-space-3);
    font-family: inherit;
    font-size: var(--ctl-font-size-md);
    line-height: 1.4;
    color: var(--ctl-color-text);
    background: transparent;
    resize: vertical;
  }
  .textarea::placeholder{
    color: var(--ctl-color-muted);
    font-weight: var(--ctl-font-weight-bold);
  }
  .textarea:focus{
    outline: none;
  }
  :host([disabled]) .textarea{
    opacity: 0.6;
    cursor: not-allowed;
  }

  .helpers{
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--ctl-space-2-5);
    padding: var(--ctl-space-2) var(--ctl-space-3);
    border-top: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface-2);
  }

  .helper{
    color: var(--ctl-color-muted);
    font-weight: var(--ctl-font-weight-bold);
    font-size: var(--ctl-font-size-xs);
    display: flex;
    align-items: center;
    gap: var(--ctl-space-1-5);
  }
  .helper:empty{ display:none; }

  .char-count{
    color: var(--ctl-color-muted);
    font-weight: var(--ctl-font-weight-extrabold);
    font-size: var(--ctl-font-size-xs);
  }
  :host([near-limit]) .char-count{
    color: var(--ctl-color-warning);
    font-weight: var(--ctl-font-weight-black);
  }
  :host([over-limit]) .char-count{
    color: var(--ctl-color-danger, #ef4444);
    font-weight: var(--ctl-font-weight-black);
  }

  .actions{
    display: flex;
    align-items: center;
    gap: var(--ctl-space-2-5);
    padding: var(--ctl-space-2-5) var(--ctl-space-3);
    border-top: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
  }

  .left-actions{
    flex: 1;
    display: flex;
    gap: var(--ctl-space-2);
  }
  .left-actions:empty{ display:none; }

  .right-actions{
    display: flex;
    gap: var(--ctl-space-2);
  }

  .iconBtn{
    all: unset;
    cursor: pointer;
    border-radius: var(--ctl-radius-10);
    padding: var(--ctl-space-2) var(--ctl-space-2-5);
    border: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface-2);
    font-size: var(--ctl-font-size-sm);
    font-weight: var(--ctl-font-weight-extrabold);
    color: var(--ctl-color-muted);
    display: flex;
    align-items: center;
    gap: var(--ctl-space-1-5);
  }
  .iconBtn:hover{ filter: brightness(.98); }
  .iconBtn:focus-visible{ outline:none; box-shadow: var(--ctl-focus-ring); }
  .iconBtn:disabled{ opacity: .5; cursor: not-allowed; }

  .sendBtn{
    all: unset;
    cursor: pointer;
    border-radius: var(--ctl-radius-10);
    padding: var(--ctl-space-2-5) var(--ctl-space-4);
    border: 1px solid var(--ctl-color-brand);
    background: var(--ctl-color-brand);
    color: var(--ctl-color-white);
    font-size: var(--ctl-font-size-md);
    font-weight: var(--ctl-font-weight-black);
    display: flex;
    align-items: center;
    gap: var(--ctl-space-1-5);
    min-height: var(--ctl-touch-target);
  }
  .sendBtn:hover{ filter: brightness(1.08); }
  .sendBtn:focus-visible{ outline:none; box-shadow: var(--ctl-focus-ring); }
  .sendBtn:disabled{ opacity: .6; cursor: not-allowed; }
  :host([sending]) .sendBtn{ opacity: .75; pointer-events: none; }

  .suggestions{
    display: flex;
    flex-wrap: wrap;
    gap: var(--ctl-space-1-5);
    padding: var(--ctl-space-2-5) var(--ctl-space-3) var(--ctl-space-3);
    border-top: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface-2);
  }
  .suggestions:empty{ display:none; }

  .suggestion{
    all: unset;
    cursor: pointer;
    border-radius: var(--ctl-radius-10);
    padding: var(--ctl-space-1-5) var(--ctl-space-2-5);
    border: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface, #fff);
    font-size: var(--ctl-font-size-sm);
    font-weight: var(--ctl-font-weight-heavy);
    color: var(--ctl-color-text);
  }
  .suggestion:hover{ filter: brightness(.98); }
  .suggestion:focus-visible{ outline:none; box-shadow: var(--ctl-focus-ring); }

  /* Hidden slots */
  ::slotted([slot="actions-left"]),
  ::slotted([slot="actions-right"]),
  ::slotted([slot="helper"]){
    display: none;
  }
</style>

<div class="frame" part="frame">
  <div class="subject-row" part="subject">
    <input 
      type="text" 
      class="subject-input" 
      id="subject" 
      placeholder="Subject" 
      aria-label="Email subject"
    />
  </div>

  <div class="compose-area" part="compose">
    <textarea 
      class="textarea" 
      id="textarea" 
      placeholder="Type your message…"
      aria-label="Message content"
      rows="4"
    ></textarea>
  </div>

  <div class="helpers" part="helpers">
    <div class="helper"><slot name="helper"></slot></div>
    <div class="char-count" id="charCount" aria-live="polite"></div>
  </div>

  <div class="suggestions" part="suggestions" id="suggestions"></div>

  <div class="actions" part="actions">
    <div class="left-actions">
      <slot name="actions-left"></slot>
    </div>
    <div class="right-actions">
      <slot name="actions-right"></slot>
      <button 
        type="button" 
        class="sendBtn" 
        id="sendBtn" 
        aria-label="Send message"
      >
        <span id="sendLabel">Send</span>
      </button>
    </div>
  </div>
</div>
`;

function isObj(v) { return v && typeof v === "object"; }
function toArray(v) { return Array.isArray(v) ? v : []; }
function safeJsonParse(str, fallback) {
  try { return JSON.parse(str); } catch { return fallback; }
}

export class CtlCompose extends HTMLElement {
  static get observedAttributes() {
    return ["value", "channel", "persona", "disabled", "sending", "max-length", "subject-enabled", "suggestions"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));

    this._textarea = this.shadowRoot.getElementById("textarea");
    this._subject = this.shadowRoot.getElementById("subject");
    this._sendBtn = this.shadowRoot.getElementById("sendBtn");
    this._sendLabel = this.shadowRoot.getElementById("sendLabel");
    this._charCount = this.shadowRoot.getElementById("charCount");
    this._suggestionsEl = this.shadowRoot.getElementById("suggestions");

    this._suggestions = [];
    this._attachments = [];

    this._onInput = this._onInput.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onClick = this._onClick.bind(this);
  }

  connectedCallback() {
    // Hydrate from JSON attrs if present
    if (this.hasAttribute("suggestions")) {
      this.suggestions = safeJsonParse(this.getAttribute("suggestions"), []);
    }

    this._textarea.addEventListener("input", this._onInput);
    this._textarea.addEventListener("keydown", this._onKeyDown);
    this._subject.addEventListener("input", this._onInput);
    this.shadowRoot.addEventListener("click", this._onClick);

    this._sync();
  }

  disconnectedCallback() {
    this._textarea.removeEventListener("input", this._onInput);
    this._textarea.removeEventListener("keydown", this._onKeyDown);
    this._subject.removeEventListener("input", this._onInput);
    this.shadowRoot.removeEventListener("click", this._onClick);
  }

  attributeChangedCallback(name) {
    if (name === "suggestions") {
      this.suggestions = safeJsonParse(this.getAttribute("suggestions"), []);
    }
    if (name === "value" && this._textarea) {
      const val = this.getAttribute("value") || "";
      if (this._textarea.value !== val) this._textarea.value = val;
    }
    this._sync();
  }

  // ---- attrs ----
  get value() { return this._textarea?.value || ""; }
  set value(v) {
    const val = String(v ?? "");
    this.setAttribute("value", val);
    if (this._textarea) this._textarea.value = val;
    this._sync();
  }

  get channel() { return this.getAttribute("channel") || "chat"; }
  set channel(v) { v === null || v === undefined ? this.removeAttribute("channel") : this.setAttribute("channel", String(v)); }

  get persona() { return this.getAttribute("persona") || ""; }
  set persona(v) { v === null || v === undefined ? this.removeAttribute("persona") : this.setAttribute("persona", String(v)); }

  get disabled() { return this.hasAttribute("disabled"); }
  set disabled(v) { v ? this.setAttribute("disabled", "") : this.removeAttribute("disabled"); }

  get sending() { return this.hasAttribute("sending"); }
  set sending(v) { v ? this.setAttribute("sending", "") : this.removeAttribute("sending"); }

  get maxLength() { 
    const raw = this.getAttribute("max-length");
    return raw ? parseInt(raw, 10) : null;
  }
  set maxLength(v) { v === null || v === undefined ? this.removeAttribute("max-length") : this.setAttribute("max-length", String(v)); }

  get subjectEnabled() { return this.hasAttribute("subject-enabled"); }
  set subjectEnabled(v) { v ? this.setAttribute("subject-enabled", "") : this.removeAttribute("subject-enabled"); }

  // ---- props ----
  get suggestions() { return this._suggestions; }
  set suggestions(v) {
    this._suggestions = toArray(v).map((s) => {
      if (typeof s === "string") return { id: s, text: s };
      return isObj(s) ? { id: String(s.id ?? ""), text: String(s.text ?? s.label ?? "") } : { id: "", text: "" };
    }).filter((s) => s.id && s.text);
    this.setAttribute("suggestions", JSON.stringify(this._suggestions));
    this._renderSuggestions();
  }

  get subject() { return this._subject?.value || ""; }
  set subject(v) {
    const val = String(v ?? "");
    if (this._subject) this._subject.value = val;
  }

  // ---- render / sync ----
  _sync() {
    if (!this._textarea) return;

    const disabled = this.disabled || this.sending;
    this._textarea.disabled = disabled;
    this._subject.disabled = disabled;
    this._sendBtn.disabled = disabled;

    // Channel-specific placeholders
    const channel = this.channel;
    if (channel === "sms") {
      this._textarea.placeholder = "Type your SMS message…";
      this._sendLabel.textContent = "Send SMS";
    } else if (channel === "email") {
      this._textarea.placeholder = "Compose your email…";
      this._sendLabel.textContent = "Send Email";
    } else if (channel === "dm") {
      this._textarea.placeholder = "Send a direct message…";
      this._sendLabel.textContent = "Send DM";
    } else {
      this._textarea.placeholder = "Type your message…";
      this._sendLabel.textContent = "Send";
    }

    // Update char count
    this._updateCharCount();
  }

  _updateCharCount() {
    const max = this.maxLength;
    if (!max || max <= 0) {
      this._charCount.textContent = "";
      this.removeAttribute("near-limit");
      this.removeAttribute("over-limit");
      return;
    }

    const len = this.value.length;
    this._charCount.textContent = `${len} / ${max}`;

    const nearThreshold = max * 0.8;
    if (len > max) {
      this.setAttribute("over-limit", "");
      this.removeAttribute("near-limit");
    } else if (len > nearThreshold) {
      this.setAttribute("near-limit", "");
      this.removeAttribute("over-limit");
    } else {
      this.removeAttribute("near-limit");
      this.removeAttribute("over-limit");
    }
  }

  _renderSuggestions() {
    if (this._suggestions.length === 0) {
      this._suggestionsEl.innerHTML = "";
      return;
    }

    const html = this._suggestions.map((s) => {
      const id = String(s.id).replace(/"/g, "&quot;");
      const text = String(s.text).replace(/</g, "&lt;").replace(/>/g, "&gt;");
      return `<button type="button" class="suggestion" data-role="suggestion" data-id="${id}">${text}</button>`;
    }).join("");

    this._suggestionsEl.innerHTML = html;
  }

  // ---- events ----
  _onInput() {
    this._updateCharCount();
    this.dispatchEvent(new CustomEvent("ctl-change", {
      bubbles: true,
      composed: true,
      detail: { value: this.value, subject: this.subject }
    }));
  }

  _onKeyDown(e) {
    // Ctrl+Enter / Cmd+Enter to send
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault();
      this._send();
    }
  }

  _onClick(e) {
    const path = e.composedPath ? e.composedPath() : [];

    // Send button
    if (path.includes(this._sendBtn)) {
      e.preventDefault();
      this._send();
      return;
    }

    // Suggestion click
    const suggestion = this._findInPath(path, (el) => el?.dataset?.role === "suggestion");
    if (suggestion) {
      const id = String(suggestion.dataset.id || "");
      const item = this._suggestions.find((s) => s.id === id);
      const text = item ? item.text : "";

      this.dispatchEvent(new CustomEvent("ctl-suggestion", {
        bubbles: true,
        composed: true,
        detail: { id, text, item }
      }));

      // Optionally auto-fill
      if (text) {
        this.value = text;
        this._textarea.focus();
      }
    }
  }

  _send() {
    if (this.disabled || this.sending) return;

    const value = this.value.trim();
    if (!value) return;

    // Validate max length
    const max = this.maxLength;
    if (max && value.length > max) {
      // Don't send if over limit; user must trim first
      return;
    }

    const detail = {
      value,
      channel: this.channel,
      persona: this.persona,
      attachments: this._attachments,
      metadata: {}
    };

    if (this.subjectEnabled) {
      detail.subject = this.subject.trim();
    }

    this.dispatchEvent(new CustomEvent("ctl-send", {
      bubbles: true,
      composed: true,
      detail
    }));

    // Clear after send (optional; caller may want to control this)
    // this.value = "";
    // this.subject = "";
  }

  _findInPath(path, predicate) {
    for (const el of path) {
      if (predicate(el)) return el;
    }
    return null;
  }

  // ---- Public API ----
  focus() {
    this._textarea?.focus();
  }

  clear() {
    this.value = "";
    this.subject = "";
    this._attachments = [];
  }
}

if (!customElements.get("ctl-compose")) {
  customElements.define("ctl-compose", CtlCompose);
}
